import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SchoolIcon from '@mui/icons-material/SchoolOutlined';
import HourglassIcon from '@mui/icons-material/HourglassTopOutlined';
import { listFinanceStudents } from '../../api/finance';
import { useBranchFilter } from '../../context/branchContext';
import { PageHeader } from '../../components/PageHeader';
import { TableToolbar } from '../../components/table/TableToolbar';
import { FinanceFilters, FinancePaymentStatus, FinanceStudentRow } from '../../types/finance';
import { ExportButtons } from './ExportButtons';
import { FinanceFilterBar } from './FinanceFilterBar';
import { formatMoney, PAYMENT_STATUS_COLOR, PAYMENT_STATUS_LABEL } from './financeFormat';
import { NumberLabel } from '../../components/NumberLabel';

const STATUSES: FinancePaymentStatus[] = ['PAID', 'PARTIALLY_PAID', 'UNPAID', 'NO_FEES'];

const idParam = (value: string | null) => (value ? Number(value) : undefined);

/** Finance → Student Payments (Phase 96): search students and see their
 * Payment Checklist balance. Only finance fields — click a row for the
 * student's payments, invoices and receipts. */
export const FinanceStudentsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedBranchId } = useBranchFilter();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 20,
  });
  const [search, setSearch] = useState('');
  const [paymentStatus, setPaymentStatus] = useState<FinancePaymentStatus | ''>(
    (searchParams.get('paymentStatus') as FinancePaymentStatus | null) ?? '',
  );
  const [pendingOnly, setPendingOnly] = useState(false);
  const [filters, setFilters] = useState<FinanceFilters>({
    universityId: idParam(searchParams.get('universityId')),
    intakeId: idParam(searchParams.get('intakeId')),
    paymentCategoryId: idParam(searchParams.get('paymentCategoryId')),
  });
  const resetPage = () => setPaginationModel((p) => ({ ...p, page: 0 }));

  const shared = {
    search: search || undefined,
    paymentStatus: paymentStatus || undefined,
    universityId: filters.universityId,
    intakeId: filters.intakeId,
    paymentCategoryId: filters.paymentCategoryId,
    branchId: selectedBranchId,
  };
  const listQuery = useQuery({
    queryKey: ['finance-students', paginationModel, shared, pendingOnly],
    queryFn: () =>
      listFinanceStudents({
        ...shared,
        pendingOnly,
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
      }),
    placeholderData: (prev) => prev,
  });

  const money = (
    field: 'required' | 'paid' | 'outstanding',
    headerName: string,
  ): GridColDef<FinanceStudentRow> => ({
    field,
    headerName,
    width: 140,
    align: 'right',
    headerAlign: 'right',
    sortable: false,
    valueFormatter: (_v, row) => formatMoney(row[field], row.currency),
  });

  const columns: GridColDef<FinanceStudentRow>[] = [
    { field: 'studentNumber', headerName: 'Student #', width: 190, renderCell: ({ row }) => <NumberLabel value={row.studentNumber} kind="student" /> },
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 160 },
    { field: 'branch', headerName: 'Branch', width: 120, valueFormatter: (v) => v ?? '—' },
    { field: 'university', headerName: 'University', width: 170, valueFormatter: (v) => v ?? '—' },
    { field: 'course', headerName: 'Course', width: 170, valueFormatter: (v) => v ?? '—' },
    { field: 'intake', headerName: 'Intake', width: 120, valueFormatter: (v) => v ?? '—' },
    {
      field: 'registeredBy',
      headerName: 'Registered By',
      description: 'The counselor who registered the student or converted their lead — not their current counselor.',
      width: 160,
      valueFormatter: (v) => v ?? '—',
    },
    money('required', 'Required'),
    money('paid', 'Paid'),
    money('outstanding', 'Outstanding'),
    {
      field: 'status',
      headerName: 'Status',
      width: 170,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: '100%' }}>
          <Chip
            size="small"
            color={PAYMENT_STATUS_COLOR[row.status]}
            label={PAYMENT_STATUS_LABEL[row.status]}
          />
          {row.hasPendingPayment && (
            <Tooltip title="A payment is awaiting confirmation">
              <HourglassIcon fontSize="small" color="info" aria-label="Awaiting confirmation" />
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={SchoolIcon}
        color="#2E7D32"
        title="Student Payments"
        subtitle="Each student's Payment Checklist balance. Open a student to record or confirm payments and issue invoices."
        actions={<ExportButtons params={{ report: 'student-balances', ...shared }} />}
      />
      <FinanceFilterBar
        value={filters}
        onChange={(next) => {
          setFilters(next);
          resetPage();
        }}
        fields={['university', 'intake', 'category']}
      >
        <TextField
          placeholder="Search name or student #…"
          size="small"
          sx={{ width: 240 }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            resetPage();
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="Payment status"
          sx={{ minWidth: 160 }}
          value={paymentStatus}
          onChange={(e) => {
            setPaymentStatus(e.target.value as FinancePaymentStatus | '');
            resetPage();
          }}
        >
          <MenuItem value="">All</MenuItem>
          {STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {PAYMENT_STATUS_LABEL[s]}
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          control={
            <Switch
              checked={pendingOnly}
              onChange={(e) => {
                setPendingOnly(e.target.checked);
                resetPage();
              }}
            />
          }
          label="Awaiting confirmation"
        />
      </FinanceFilterBar>

      {listQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => listQuery.refetch()}>
              Retry
            </Button>
          }
        >
          Couldn't load students.
        </Alert>
      ) : (
        <Box sx={{ height: 620, bgcolor: 'background.paper', borderRadius: 1 }}>
          <DataGrid
            rows={listQuery.data?.data ?? []}
            columns={columns}
            rowCount={listQuery.data?.meta.total ?? 0}
            loading={listQuery.isFetching}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 20, 50, 100]}
            slots={{ toolbar: TableToolbar }}
            localeText={{ noRowsLabel: 'No students match these filters.' }}
            disableRowSelectionOnClick
            onRowClick={(params) => navigate(`/finance/students/${params.row.id}`)}
            sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
          />
        </Box>
      )}
    </Box>
  );
};
